import { bookByName } from "./canon.ts";

/**
 * Round 1 tree: Testament → division → (optional sub-group) → book.
 * Leaves carry a `bookId`; leaf order equals canonical book order, which is what
 * makes the "Look Above / Look Below" hints meaningful at every level.
 */
export interface HierarchyNode {
  name: string;
  description?: string;
  bookId?: number;
  children?: HierarchyNode[];
}

const book = (name: string): HierarchyNode => ({ name, bookId: bookByName(name).id });
const books = (...names: string[]): HierarchyNode[] => names.map(book);
const group = (name: string, description: string, children: HierarchyNode[]): HierarchyNode => ({ name, description, children });

export const HIERARCHY: readonly HierarchyNode[] = [
  group("Old Testament", "The scriptures of Israel, written before Christ", [
    group("Law", "Creation, the patriarchs, the exodus, and the covenant given at Sinai", books("Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy")),
    group("History", "Israel's story from entering the land through exile and return", [
      group("Settling the land", "Conquest, the judges, and a family in a dark age", books("Joshua", "Judges", "Ruth")),
      group("The kings", "From the first king to the fall of Jerusalem", books("1 Samuel", "2 Samuel", "1 Kings", "2 Kings")),
      group("Chronicles and return", "A priestly retelling, then rebuilding after exile", books("1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", "Esther")),
    ]),
    group("Wisdom and Poetry", "Songs, sayings, and hard questions about living well", books("Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon")),
    group("Prophets", "Messengers who called Israel back to the covenant", [
      group("Major Prophets", "The longer prophetic books", books("Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel")),
      group("Minor Prophets", "Twelve shorter prophetic books", books("Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi")),
    ]),
  ]),
  group("New Testament", "The message of Jesus and his followers, written in the first century", [
    group("Gospels", "Four accounts of the life of Jesus", books("Matthew", "Mark", "Luke", "John")),
    book("Acts"),
    group("Paul's Letters", "Letters from the apostle Paul", [
      group("To churches", "Written to whole congregations", books("Romans", "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians", "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians")),
      group("To individuals", "Written to co-workers and a friend", books("1 Timothy", "2 Timothy", "Titus", "Philemon")),
    ]),
    group("General Letters", "Letters from other apostles and leaders", books("Hebrews", "James", "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude")),
    book("Revelation"),
  ]),
];

/** Books in hierarchy order (depth-first). Must equal canonical order. */
export function hierarchyLeaves(nodes: readonly HierarchyNode[] = HIERARCHY): number[] {
  return nodes.flatMap((n) => (n.children ? hierarchyLeaves(n.children) : n.bookId !== undefined ? [n.bookId] : []));
}

/** Child indices leading to a book, e.g. Romans → [1, 2, 0, 0]. */
export function hierarchyPathForBook(bookId: number, nodes: readonly HierarchyNode[] = HIERARCHY): number[] {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    if (n.bookId === bookId) return [i];
    if (n.children) {
      const sub = hierarchyPathForBook(bookId, n.children);
      if (sub.length) return [i, ...sub];
    }
  }
  return [];
}

/** Node reached by following `path` from the top level; undefined if the path leaves the tree. */
export function hierarchyNodeAt(path: readonly number[], nodes: readonly HierarchyNode[] = HIERARCHY): HierarchyNode | undefined {
  let level: readonly HierarchyNode[] | undefined = nodes;
  let node: HierarchyNode | undefined;
  for (const i of path) {
    node = level?.[i];
    if (!node) return undefined;
    level = node.children;
  }
  return node;
}

/** Options shown at the level reached by `prefix` (empty prefix = testaments). */
export function hierarchyOptions(prefix: readonly number[], nodes: readonly HierarchyNode[] = HIERARCHY): readonly HierarchyNode[] | undefined {
  if (prefix.length === 0) return nodes;
  return hierarchyNodeAt(prefix, nodes)?.children;
}
